import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import moment, { type Moment } from 'moment';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { collection, getDocs } from 'firebase/firestore';
import { message, notification } from 'antd';

import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import { WORK_JOURNEY_PLACE_TYPE } from '@src/packages/workJourney/constants';
import type { IWorkJourneyReplacementCandidate } from '@src/models/interfaces/workJourneyManagement.interface';
import { useFirestoreQuery } from '@src/packages/utils';
import { resolveUserDisplayName } from '@src/packages/utils/user';
import type { WorkJourneyAddress, WorkJourneyWorkShift } from '@src/packages/workJourney/types';
import WorkJourneyManagementService from '@src/services/WorkJourneyManagementService';

import type {
  PlanMonthlyJourneyAddressOption,
  PlanMonthlyJourneyConfigOption,
  PlanMonthlyJourneyFormState,
  PlanMonthlyJourneyMember,
  PlanMonthlyJourneyShiftOption,
  PlanMonthlyJourneyStep,
  PlanMonthlyJourneySupervisor,
  PlanMonthlyJourneyUnderMinImpact,
} from '../types';
import { evaluatePlanMemberMoveImpact } from '../utils/evaluatePlanMemberMoveImpact';
import { formatPlanShiftLabel } from '../utils/formatPlanShiftLabel';
import {
  isPlanStartDateAllowed,
  resolvePlanEndDateIso,
  resolvePlanStartDateIso,
} from '../utils/resolvePlanPeriod';
import { dedupeById, sanitizePlanFirestorePayload } from '../utils/sanitizePlanPayload';

const EMPTY_FORM: PlanMonthlyJourneyFormState = {
  workShiftId: null,
  workJourneyConfigId: null,
  startDate: null,
  addressId: null,
  supervisors: [],
  requiredMembers: 1,
  members: [],
};

const toMemberFromOrgUser = (
  user: Record<string, unknown>,
  previousWorkJourney?: PlanMonthlyJourneyMember['previousWorkJourney'],
  addedInPlan = false,
): PlanMonthlyJourneyMember => {
  const id = String(user.id || '');
  const displayName =
    resolveUserDisplayName({
      firstName: typeof user.firstName === 'string' ? user.firstName : undefined,
      lastName: typeof user.lastName === 'string' ? user.lastName : undefined,
      displayName: typeof user.displayName === 'string' ? user.displayName : undefined,
    }) || String(user.email || id);
  const job =
    user.job && typeof user.job === 'object'
      ? {
          id: String((user.job as { id?: string }).id || ''),
          name: String((user.job as { name?: string }).name || ''),
        }
      : null;

  return {
    id,
    displayName,
    idCard: (user.idCard as string | null | undefined) ?? null,
    jurisdictionId: user.jurisdictionId as PlanMonthlyJourneyMember['jurisdictionId'],
    photoURL: (user.photoURL as string | null | undefined) ?? null,
    job,
    previousWorkJourney: previousWorkJourney ?? null,
    addedInPlan,
  };
};

const toMemberFromCandidate = (
  candidate: IWorkJourneyReplacementCandidate,
): PlanMonthlyJourneyMember => ({
  id: candidate.id,
  displayName: candidate.displayName || candidate.email || candidate.id,
  idCard: candidate.idCard ?? null,
  jurisdictionId: candidate.jurisdictionId,
  photoURL: candidate.photoURL ?? null,
  job: candidate.job ?? null,
  previousWorkJourney: candidate.currentWorkJourney ?? null,
  addedInPlan: true,
});

type UsePlanMonthlyJourneyParams = {
  open: boolean;
  initialWorkJourneyConfigId?: string | null;
  onClose: () => void;
  onSaved?: () => void;
};

export const usePlanMonthlyJourney = ({
  open,
  initialWorkJourneyConfigId,
  onClose,
  onSaved,
}: UsePlanMonthlyJourneyParams) => {
  const intl = useIntl();
  const organizationId = useSelector(
    ({ organizations }: { organizations: { organization: { id?: string } } }) =>
      organizations.organization?.id,
  );

  const managementServiceRef = useRef(new WorkJourneyManagementService());

  const [step, setStep] = useState<PlanMonthlyJourneyStep>(1);
  const [form, setForm] = useState<PlanMonthlyJourneyFormState>(EMPTY_FORM);
  const [configsById, setConfigsById] = useState<Record<string, PlanMonthlyJourneyConfigOption>>(
    {},
  );
  const [configsLoading, setConfigsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [hadMovedMembers, setHadMovedMembers] = useState(false);

  const [addUsersOpen, setAddUsersOpen] = useState(false);
  const [underMinImpact, setUnderMinImpact] = useState<PlanMonthlyJourneyUnderMinImpact | null>(
    null,
  );
  const [pendingCandidate, setPendingCandidate] =
    useState<IWorkJourneyReplacementCandidate | null>(null);

  const shiftsQuery = useMemo(() => {
    if (!organizationId || !open) return null;
    return collection(db, FSCollections.ORGANIZATIONS, organizationId, FSCollections.WORK_SHIFTS);
  }, [organizationId, open]);

  const addressesQuery = useMemo(() => {
    if (!organizationId || !open) return null;
    return collection(db, FSCollections.ORGANIZATIONS, organizationId, FSCollections.ADDRESSES);
  }, [organizationId, open]);

  const { data: rawShifts = [], loading: shiftsLoading } = useFirestoreQuery(shiftsQuery, [
    organizationId,
    open,
  ]) as { data?: WorkJourneyWorkShift[]; loading: boolean };

  const { data: rawAddresses = [], loading: addressesLoading } = useFirestoreQuery(addressesQuery, [
    organizationId,
    open,
  ]) as { data?: WorkJourneyAddress[]; loading: boolean };

  const shiftOptions: PlanMonthlyJourneyShiftOption[] = useMemo(
    () =>
      (rawShifts || [])
        .filter(shift => !!shift?.id)
        .map(shift => ({
          id: String(shift.id),
          label: formatPlanShiftLabel(shift),
          raw: shift as unknown as Record<string, unknown>,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
    [rawShifts],
  );

  const addressOptions: PlanMonthlyJourneyAddressOption[] = useMemo(
    () =>
      (rawAddresses || [])
        .filter(address => !!address?.id)
        .map(address => ({
          id: String(address.id),
          label: String(address.address || address.id),
          raw: address as unknown as Record<string, unknown>,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
    [rawAddresses],
  );

  const loadConfigs = useCallback(async () => {
    if (!organizationId || !open) return;
    setConfigsLoading(true);
    try {
      const configsRef = collection(
        db,
        FSCollections.ORGANIZATIONS,
        organizationId,
        FSCollections.WORK_JOURNEY_CONFIGS,
      );
      const snapshot = await getDocs(configsRef);
      const next: Record<string, PlanMonthlyJourneyConfigOption> = {};

      await Promise.all(
        snapshot.docs.map(async docSnap => {
          const data = docSnap.data() as Record<string, unknown>;
          const workShift = data.workShift as WorkJourneyWorkShift | null | undefined;
          const membersSnap = await getDocs(
            collection(
              db,
              FSCollections.ORGANIZATIONS,
              organizationId,
              FSCollections.WORK_JOURNEY_CONFIGS,
              docSnap.id,
              FSCollections.WORK_JOURNEY_MEMBERS,
            ),
          );
          const supervisorsSnap = await getDocs(
            collection(
              db,
              FSCollections.ORGANIZATIONS,
              organizationId,
              FSCollections.WORK_JOURNEY_CONFIGS,
              docSnap.id,
              FSCollections.WORK_JOURNEY_SUPERVISORS,
            ),
          );

          const members = dedupeById(
            membersSnap.docs
              .map(memberDoc => {
                const memberData = memberDoc.data() as { user?: Record<string, unknown> };
                if (!memberData.user?.id) return null;
                return toMemberFromOrgUser(memberData.user, {
                  workJourneyConfigId: docSnap.id,
                  workJourneyConfigName: String(data.name || docSnap.id),
                });
              })
              .filter((member): member is PlanMonthlyJourneyMember => !!member),
          );

          const supervisors = dedupeById(
            supervisorsSnap.docs
              .map(supervisorDoc => {
                const supervisorData = supervisorDoc.data() as { user?: Record<string, unknown> };
                const user = supervisorData.user;
                if (!user?.id) return null;
                return {
                  id: String(user.id),
                  displayName:
                    resolveUserDisplayName({
                      firstName: typeof user.firstName === 'string' ? user.firstName : undefined,
                      lastName: typeof user.lastName === 'string' ? user.lastName : undefined,
                      displayName:
                        typeof user.displayName === 'string' ? user.displayName : undefined,
                    }) || String(user.email || user.id),
                  idCard: (user.idCard as string | null | undefined) ?? null,
                  photoURL: (user.photoURL as string | null | undefined) ?? null,
                  email: (user.email as string | null | undefined) ?? null,
                } as PlanMonthlyJourneySupervisor;
              })
              .filter((supervisor): supervisor is PlanMonthlyJourneySupervisor => !!supervisor),
          );

          next[docSnap.id] = {
            id: docSnap.id,
            name: String(data.name || docSnap.id),
            workShiftId: workShift?.id ? String(workShift.id) : null,
            requiredMembers: Number(data.requiredMembers) || 0,
            address: (data.address as Record<string, unknown> | null) ?? null,
            shiftDescription: formatPlanShiftLabel(workShift),
            members,
            supervisors,
          };
        }),
      );

      setConfigsById(next);
    } catch (error) {
      console.error('Error loading journey configs for plan flow:', error);
      setConfigsById({});
    } finally {
      setConfigsLoading(false);
    }
  }, [organizationId, open]);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setForm(EMPTY_FORM);
    setSuccessOpen(false);
    setHadMovedMembers(false);
    setAddUsersOpen(false);
    setUnderMinImpact(null);
    setPendingCandidate(null);
    loadConfigs();
  }, [open, loadConfigs]);

  // Prefill journey (and its shift/address/members/supervisors) once configs load.
  useEffect(() => {
    if (!open || !initialWorkJourneyConfigId || configsLoading) return;
    const config = configsById[initialWorkJourneyConfigId];
    if (!config) return;
    setForm(prev => ({
      ...prev,
      workShiftId: config.workShiftId ?? prev.workShiftId,
      workJourneyConfigId: config.id,
      addressId:
        config.address && typeof config.address.id === 'string'
          ? String(config.address.id)
          : prev.addressId,
      supervisors: config.supervisors,
      requiredMembers: Math.max(1, config.requiredMembers || 1),
      members: config.members.map(member => ({ ...member, addedInPlan: false })),
      startDate: prev.startDate ?? moment().startOf('day'),
    }));
  }, [open, initialWorkJourneyConfigId, configsById, configsLoading]);

  const journeyOptions = useMemo(() => {
    const all = Object.values(configsById);
    if (!form.workShiftId) return all;
    return all.filter(config => config.workShiftId === form.workShiftId);
  }, [configsById, form.workShiftId]);

  const selectedConfig = form.workJourneyConfigId
    ? configsById[form.workJourneyConfigId] ?? null
    : null;

  const selectedShiftLabel =
    shiftOptions.find(option => option.id === form.workShiftId)?.label ||
    selectedConfig?.shiftDescription ||
    '—';

  const selectedAddressLabel =
    addressOptions.find(option => option.id === form.addressId)?.label ||
    (selectedConfig?.address && typeof selectedConfig.address.address === 'string'
      ? String(selectedConfig.address.address)
      : '—');

  const patchForm = useCallback((patch: Partial<PlanMonthlyJourneyFormState>) => {
    setForm(prev => ({ ...prev, ...patch }));
  }, []);

  const handleShiftChange = useCallback(
    (workShiftId: string) => {
      setForm(prev => ({
        ...prev,
        workShiftId,
        workJourneyConfigId:
          prev.workJourneyConfigId &&
          configsById[prev.workJourneyConfigId]?.workShiftId === workShiftId
            ? prev.workJourneyConfigId
            : null,
        members: [],
        supervisors: [],
        requiredMembers: 1,
        addressId: null,
      }));
    },
    [configsById],
  );

  const handleJourneyChange = useCallback(
    (workJourneyConfigId: string) => {
      const config = configsById[workJourneyConfigId];
      if (!config) {
        patchForm({ workJourneyConfigId });
        return;
      }
      setForm(prev => ({
        ...prev,
        workJourneyConfigId,
        workShiftId: config.workShiftId || prev.workShiftId,
        requiredMembers: Math.max(1, config.requiredMembers || 1),
        addressId:
          config.address && typeof config.address.id === 'string'
            ? String(config.address.id)
            : prev.addressId,
        supervisors: config.supervisors,
        members: config.members.map(member => ({ ...member, addedInPlan: false })),
      }));
    },
    [configsById, patchForm],
  );

  const canContinueStep1 =
    !!form.workShiftId &&
    !!form.workJourneyConfigId &&
    !!form.startDate &&
    isPlanStartDateAllowed(form.startDate) &&
    !!form.addressId &&
    form.supervisors.length > 0;

  const canContinueStep2 = form.requiredMembers > 0 && form.members.length > 0;

  const goNext = useCallback(() => {
    if (step === 1) {
      if (!canContinueStep1) {
        message.warning(
          intl.formatMessage({ id: 'workJourney.management.plan.validation.step1' }),
        );
        return;
      }
      setStep(2);
      return;
    }
    if (step === 2) {
      if (!canContinueStep2) {
        message.warning(
          intl.formatMessage({ id: 'workJourney.management.plan.validation.step2' }),
        );
        return;
      }
      setStep(3);
    }
  }, [step, canContinueStep1, canContinueStep2, intl]);

  const goBack = useCallback(() => {
    if (step === 1) {
      onClose();
      return;
    }
    setStep(prev => (prev === 3 ? 2 : 1));
  }, [step, onClose]);

  const removeMember = useCallback((userId: string) => {
    setForm(prev => ({
      ...prev,
      members: prev.members.filter(member => member.id !== userId),
    }));
  }, []);

  const commitCandidate = useCallback((candidate: IWorkJourneyReplacementCandidate) => {
    setForm(prev => {
      if (prev.members.some(member => member.id === candidate.id)) return prev;
      return {
        ...prev,
        members: [...prev.members, toMemberFromCandidate(candidate)],
      };
    });
    setPendingCandidate(null);
    setUnderMinImpact(null);
  }, []);

  const requestAddCandidate = useCallback(
    async (candidate: IWorkJourneyReplacementCandidate) => {
      if (form.members.some(member => member.id === candidate.id)) return;

      const previous = candidate.currentWorkJourney;
      if (
        !previous?.workJourneyConfigId ||
        previous.workJourneyConfigId === form.workJourneyConfigId
      ) {
        commitCandidate(candidate);
        return;
      }

      try {
        const [assigned, snapshot] = await Promise.all([
          managementServiceRef.current.getWorkJourneyMembersCount(
            organizationId!,
            previous.workJourneyConfigId,
          ),
          managementServiceRef.current.getWorkJourneyConfigSnapshot(
            organizationId!,
            previous.workJourneyConfigId,
          ),
        ]);
        const impact = evaluatePlanMemberMoveImpact({
          candidate,
          previousAssignedMembers: assigned,
          previousRequiredMembers: snapshot?.requiredMembers ?? 0,
        });
        if (impact) {
          setPendingCandidate(candidate);
          setUnderMinImpact(impact);
          return;
        }
      } catch (error) {
        console.error('Error evaluating plan member move impact:', error);
      }
      commitCandidate(candidate);
    },
    [commitCandidate, form.members, form.workJourneyConfigId, organizationId],
  );

  const confirmUnderMin = useCallback(() => {
    if (pendingCandidate) commitCandidate(pendingCandidate);
  }, [commitCandidate, pendingCandidate]);

  const cancelUnderMin = useCallback(() => {
    setPendingCandidate(null);
    setUnderMinImpact(null);
  }, []);

  const handleSave = useCallback(async () => {
    if (!organizationId || !form.workJourneyConfigId || !selectedConfig) return;
    if (!canContinueStep1 || !canContinueStep2) return;

    setSaving(true);
    try {
      const startDateIso = resolvePlanStartDateIso(form.startDate);
      const endDateIso = resolvePlanEndDateIso(form.startDate);
      if (!startDateIso || !endDateIso) {
        throw new Error('Invalid plan period');
      }

      const address = sanitizePlanFirestorePayload(
        addressOptions.find(option => option.id === form.addressId)?.raw ||
          selectedConfig.address ||
          null,
      );

      const membersPayload = dedupeById(form.members).map(member => ({
        id: member.id,
        organizationId,
        user: {
          id: member.id,
          displayName: member.displayName,
          idCard: member.idCard,
          jurisdictionId: member.jurisdictionId,
          photoURL: member.photoURL,
          job: member.job,
        },
      }));

      const supervisorsPayload = dedupeById(form.supervisors).map(supervisor => ({
        id: supervisor.id,
        user: {
          id: supervisor.id,
          displayName: supervisor.displayName,
          idCard: supervisor.idCard,
          photoURL: supervisor.photoURL,
          email: supervisor.email,
        },
      }));

      const movedMembers = dedupeById(form.members)
        .filter(
          member =>
            member.addedInPlan &&
            member.previousWorkJourney?.workJourneyConfigId &&
            member.previousWorkJourney.workJourneyConfigId !== form.workJourneyConfigId,
        )
        .map(member => ({
          userId: member.id,
          fromWorkJourneyConfigId: member.previousWorkJourney!.workJourneyConfigId,
          fromWorkJourneyConfigName: member.previousWorkJourney!.workJourneyConfigName,
        }));

      const requiredMembers = Math.max(1, Math.floor(Number(form.requiredMembers)) || 1);

      const result = await managementServiceRef.current.planMonthlyJourney({
        organizationId,
        workJourneyConfigId: form.workJourneyConfigId,
        requiredMembers,
        address,
        workShift: sanitizePlanFirestorePayload(
          (shiftOptions.find(option => option.id === form.workShiftId)?.raw as Record<
            string,
            unknown
          >) || null,
        ),
        locationType: WORK_JOURNEY_PLACE_TYPE.PRESENTIAL,
        shiftStartDate: startDateIso,
        shiftEndDate: endDateIso,
        supervisors: supervisorsPayload,
        members: membersPayload,
        movedMembers,
      });

      if (!result.success) {
        throw new Error(result.message || 'planMonthlyJourney failed');
      }

      setHadMovedMembers(result.pendingNotices > 0 || movedMembers.length > 0);
      setSuccessOpen(true);
      onSaved?.();
    } catch (error) {
      console.error('Error saving monthly journey plan:', error);
      const serverMessage =
        error && typeof error === 'object' && 'message' in error
          ? String((error as { message?: unknown }).message || '').trim()
          : '';
      notification.error({
        message:
          serverMessage || intl.formatMessage({ id: 'general.save.error.message' }),
      });
    } finally {
      setSaving(false);
    }
  }, [
    addressOptions,
    canContinueStep1,
    canContinueStep2,
    form,
    intl,
    onSaved,
    organizationId,
    selectedConfig,
    shiftOptions,
  ]);

  const handleSuccessBackHome = useCallback(() => {
    setSuccessOpen(false);
    onClose();
  }, [onClose]);

  const handleSuccessPlanAnother = useCallback(() => {
    setSuccessOpen(false);
    setStep(1);
    setForm(EMPTY_FORM);
    setHadMovedMembers(false);
    loadConfigs();
  }, [loadConfigs]);

  const setStartDate = useCallback((startDate: Moment | null) => {
    patchForm({ startDate });
  }, [patchForm]);

  const setRequiredMembers = useCallback((requiredMembers: number) => {
    patchForm({ requiredMembers: Math.max(1, requiredMembers) });
  }, [patchForm]);

  const setSupervisors = useCallback((supervisors: PlanMonthlyJourneySupervisor[]) => {
    patchForm({ supervisors });
  }, [patchForm]);

  const setAddressId = useCallback((addressId: string) => {
    patchForm({ addressId });
  }, [patchForm]);

  return {
    intl,
    organizationId,
    step,
    form,
    shiftOptions,
    journeyOptions,
    addressOptions,
    selectedConfig,
    selectedShiftLabel,
    selectedAddressLabel,
    shiftsLoading,
    addressesLoading,
    configsLoading,
    saving,
    successOpen,
    hadMovedMembers,
    addUsersOpen,
    setAddUsersOpen,
    underMinImpact,
    canContinueStep1,
    canContinueStep2,
    handleShiftChange,
    handleJourneyChange,
    setStartDate,
    setAddressId,
    setSupervisors,
    setRequiredMembers,
    removeMember,
    requestAddCandidate,
    confirmUnderMin,
    cancelUnderMin,
    goNext,
    goBack,
    handleSave,
    handleSuccessBackHome,
    handleSuccessPlanAnother,
    planStartIso: resolvePlanStartDateIso(form.startDate),
    planEndIso: resolvePlanEndDateIso(form.startDate),
  };
};
